import { useState } from 'react';
import { Button } from '@/components/ui/button';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { useTranslations } from '@/i18n';
import { useActivateRoaming } from '@/hooks/trips/useActivateRoaming';
import { useRouter } from '@/lib/navigation';
import { cn } from '@/lib/utils';

interface TripRoamingActivationCardProps {
  tripId: string;
  phoneNumber?: string | null;
  isActivated?: boolean | null;
}

export function TripRoamingActivationCard({
  tripId,
  phoneNumber,
  isActivated,
}: TripRoamingActivationCardProps) {
  const t = useTranslations('tripRoaming');
  const tToasts = useTranslations('toasts');
  const activateRoaming = useActivateRoaming();
  const [showConfirm, setShowConfirm] = useState(false);
  const { showToast } = useToast();
  const router = useRouter();

  const handleConfirm = () => {
    activateRoaming.mutate(
      { tripId },
      {
        onSuccess: () => {
          setShowConfirm(false);
          showToast({
            message: tToasts('roamingActivated'),
            variant: 'success',
          });
          router.push('/approvals');
        },
        onError: () => {
          setShowConfirm(false);
        },
      }
    );
  };

  const displayPhone = phoneNumber && phoneNumber.trim().length > 0 ? phoneNumber : t('noPhone');
  const activationConfirmed = Boolean(isActivated);
  const isDisabled = activationConfirmed || activateRoaming.isPending;

  return (
    <div className="rounded-3xl border border-[#f1f1f1] bg-white px-4 py-5 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] md:px-8 md:py-6">
      <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-start gap-4 sm:items-center">
          <div className="flex shrink-0 items-center justify-center rounded-full border border-[#f1f1f1] bg-[#f9f9f9] p-2">
            <img src="/assets/icons/phone-wave.svg" alt="" className="h-6 w-6" />
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-[18px] leading-7 font-medium text-[#292929]">{t('title')}</span>
            <span className="text-base leading-6 text-[#666666]">{displayPhone}</span>
          </div>
        </div>

        <Button
          variant="default"
          disabled={isDisabled}
          onClick={() => setShowConfirm(true)}
          className={cn(
            'flex h-12 w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-medium text-white transition-transform hover:scale-[0.99] disabled:cursor-not-allowed disabled:text-white disabled:opacity-50 md:w-auto',
            activationConfirmed
              ? 'bg-[#30a46c] disabled:bg-[#30a46c]'
              : 'bg-[#171716] disabled:bg-[#171716]'
          )}
        >
          {activateRoaming.isPending ? (
            <Spinner variant="circle" size={16} strokeWidth={2} className="text-white" />
          ) : null}
          {isActivated ? t('activated') : t('activate')}
        </Button>
      </div>

      <ConfirmationDialog
        isOpen={showConfirm}
        onClose={() => {
          if (!activateRoaming.isPending) {
            setShowConfirm(false);
          }
        }}
        onConfirm={handleConfirm}
        title={t('confirmTitle')}
        message={t('confirmMessage')}
        confirmText={t('confirmButton')}
        cancelText={t('cancelButton')}
        isLoading={activateRoaming.isPending}
      />
    </div>
  );
}

export default TripRoamingActivationCard;
